import {
  type BillingDetail,
  type Config,
  type DeployActivePlan,
  type DeployApp,
  type DeployAppConfig,
  type DeployBackup,
  type DeployBackupRetentionPolicy,
  type DeployCertificate,
  type DeployContainer,
  type DeployCost,
  type DeployCostRates,
  type DeployCustomerControl,
  type DeployCustomerControlResource,
  type DeployDashboard,
  type DeployDatabase,
  type DeployDatabaseCredential,
  type DeployDatabaseImage,
  type DeployDisk,
  type DeployDiskAttachment,
  type DeployEdge,
  type DeployEndpoint,
  type DeployEnvironment,
  type DeployEnvironmentStats,
  type DeployGlobalControl,
  type DeployGlobalControlFramework,
  type DeployImage,
  type DeployLlmGatewayConfiguration,
  type DeployLlmGatewayKey,
  type DeployLlmKey,
  type DeployLlmKeyRequest,
  type DeployLlmKeyUser,
  type DeployLlmPolicy,
  type DeployLlmTraceDrain,
  type DeployLogDrain,
  type DeployMaintenanceWindow,
  type DeployMetricDrain,
  type DeployOperation,
  type DeployPersistentDisk,
  type DeployPlan,
  type DeployRelease,
  type DeployService,
  type DeployServiceDefinition,
  type DeployServiceSizingPolicy,
  type DeployServiceSizingPolicyScheduleV2,
  type DeploySetting,
  type DeploySource,
  type DeployStack,
  type DeployVpcPeer,
  type DeployVpnTunnel,
  type Deployment,
  type InstanceClass,
  type Invitation,
  type LlmGatewayRequest,
  type LlmGatewayRequestPage,
  type LlmGatewayUsage,
  type ManualScaleRecommendation,
  type Membership,
  ModalType,
  type Organization,
  type Otp,
  type Permission,
  type Role,
  type Theme,
  type Token,
  type User,
  type UserAgreement,
} from "@app/types";

export const DEFAULT_INSTANCE_CLASS: InstanceClass = "m";

export const defaultConfig = (e: Partial<Config> = {}): Config => {
  return {
    isProduction: import.meta.env.PROD,
    isDev: import.meta.env.DEV,
    isTest: false,
    appUrl: import.meta.env.VITE_APP_URL || "",
    authUrl: import.meta.env.VITE_AUTH_URL || "",
    billingUrl: import.meta.env.VITE_BILLING_URL || "",
    apiUrl: import.meta.env.VITE_API_URL || "",
    metricTunnelUrl: import.meta.env.VITE_METRIC_TUNNEL_URL || "",
    freezerUrl: import.meta.env.VITE_FREEZER_URL || "",
    llmGatewayUrl: import.meta.env.VITE_LLM_GATEWAY_URL || "",
    llmGatewayV2Url: import.meta.env.VITE_LLM_GATEWAY_V2_URL || "",
    sentryDsn: import.meta.env.VITE_SENTRY_DSN || "",
    origin: (import.meta.env.VITE_ORIGIN as any) || "app",
    aptibleOrgId:
      import.meta.env.VITE_APTIBLE_ORG_ID ||
      "df0ee681-9e02-4c28-8916-3b215d539b08",
    betaFeatureOrgIds:
      import.meta.env.VITE_FEATURE_BETA_ORG_IDS ||
      "df0ee681-9e02-4c28-8916-3b215d539b08",
    externalAwsFeatureOrgIds:
      import.meta.env.VITE_FEATURE_ACCOUNT_INTEGRATIONS_ORG_IDS ||
      "df0ee681-9e02-4c28-8916-3b215d539b08",
    legacyDashboardUrl:
      import.meta.env.VITE_LEGACY_DASHBOARD_URL ||
      "https://dashboard.aptible.com",
    mintlifyChatKey:
      import.meta.env.VITE_MINTLIFY_CHAT_KEY ||
      "mint_dsc_XXXXXXXXXXXXXXXXXXXXXXXX",
    stripePublishableKey:
      import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ||
      "pk_test_eiw5HXHTAgTwyNnV9I5ruCrA",
    scheduledHasOrgIds: import.meta.env.VITE_SCHEDULED_HAS_ORG_IDS || "",
    maintenanceWindowOrgIds:
      import.meta.env.VITE_MAINTENANCE_WINDOW_ORG_IDS || "",
    labsFlagDetectionOrgIds:
      import.meta.env.VITE_FEATURE_LABS_FLAG_DETECTION_ORG_IDS ||
      "afc1bc92-13c8-4848-bdfa-355ec07a4f7f,df0ee681-9e02-4c28-8916-3b215d539b08",
    ...e,
  };
};

export const defaultDeployApp = (a: Partial<DeployApp> = {}): DeployApp => {
  const now = new Date().toISOString();
  return {
    id: "",
    handle: "",
    gitRepo: "",
    createdAt: now,
    updatedAt: now,
    deploymentMethod: "",
    status: "unknown",
    environmentId: "",
    currentConfigurationId: "",
    currentDeploymentId: "",
    currentImageId: "",
    currentSourceId: "",
    currentSettingId: "",
    maintenanceWindowId: "",
    ...a,
  };
};

export const defaultDeployAppConfig = (
  a: Partial<DeployAppConfig> = {},
): DeployAppConfig => {
  return {
    id: "",
    env: {},
    appId: "",
    ...a,
  };
};

export const defaultDeployCertificate = (
  c: Partial<DeployCertificate> = {},
): DeployCertificate => {
  const now = new Date().toISOString();
  return {
    id: "",
    commonName: "",
    certificateBody: "",
    notBefore: now,
    notAfter: now,
    issuerCountry: "",
    issuerOrganization: "",
    issuerWebsite: "",
    issuerCommonName: "",
    subjectCountry: "",
    subjectState: "",
    subjectLocale: "",
    subjectOrganization: "",
    acme: false,
    leafCertificate: "",
    certificateChain: "",
    sha256Fingerprint: "",
    trusted: false,
    selfSigned: true,
    subjectAlternativeNames: [],
    privateKeyAlgorithm: "",
    privateKey: "",
    createdAt: now,
    updatedAt: now,
    environmentId: "",
    ...c,
  };
};

export const defaultDeployEndpoint = (
  e: Partial<DeployEndpoint> = {},
): DeployEndpoint => {
  const now = new Date().toISOString();
  return {
    id: "",
    status: "pending",
    acme: false,
    acmeConfiguration: null,
    acmeDnsChallengeHost: "",
    acmeStatus: "pending",
    containerExposedPorts: [],
    containerPort: "",
    containerPorts: [],
    default: false,
    dockerName: "",
    externalHost: "",
    externalHttpPort: "",
    externalHttpsPort: "",
    internal: false,
    ipWhitelist: [],
    platform: "elb",
    shared: false,
    type: "unknown",
    tokenHeader: undefined,
    lbAlgorithm: undefined,
    currentSettingId: "",
    createdAt: now,
    updatedAt: now,
    userDomain: "",
    virtualDomain: "",
    serviceId: "",
    certificateId: "",
    ...e,
  };
};

export const defaultDeployEnvironment = (
  e: Partial<DeployEnvironment> = {},
): DeployEnvironment => {
  const now = new Date().toISOString();
  return {
    id: "",
    organizationId: "",
    handle: "Unknown",
    createdAt: now,
    updatedAt: now,
    type: "development",
    activated: true,
    totalAppCount: 0,
    totalDatabaseCount: 0,
    totalBackupSize: 0,
    sweetnessStack: "",
    stackId: "",
    onboardingStatus: "unknown",
    ...e,
  };
};

export const defaultDeployEnvironmentStats = (
  e: Partial<DeployEnvironmentStats> = {},
): DeployEnvironmentStats => {
  return {
    id: "",
    containerCount: 0,
    domainCount: 0,
    totalDiskSize: 0,
    appContainerCount: 0,
    databaseContainerCount: 0,
    totalBackupSize: 0,
    ...e,
  };
};

export const defaultDeployServiceDefinition = (
  e: Partial<DeployServiceDefinition> = {},
): DeployServiceDefinition => {
  const now = new Date().toISOString();
  return {
    createdAt: now,
    updatedAt: now,
    id: "",
    appId: "",
    command: "",
    processType: "",
    ...e,
  };
};

export const defaultDeployStack = (
  s: Partial<DeployStack> = {},
): DeployStack => {
  const now = new Date().toISOString();
  return {
    id: "",
    organizationId: "",
    name: "",
    region: "",
    default: false,
    public: false,
    createdAt: now,
    updatedAt: now,
    outboundIpAddresses: [],
    memoryLimits: false,
    cpuLimits: false,
    intrusionDetection: false,
    exposeIntrusionDetectionReports: false,
    allowCInstanceProfile: false,
    allowMInstanceProfile: false,
    allowRInstanceProfile: false,
    allowTInstanceProfile: false,
    allowGranularContainerSizes: false,
    verticalAutoscaling: false,
    horizontalAutoscaling: false,
    internalDomain: "aptible.in",
    defaultDomain: "on-aptible.com",
    selfHosted: false,
    awsAccountId: "",
    vpcId: "",
    vpcCidr: "",
    ...s,
  };
};

export const defaultDeployDisk = (d: Partial<DeployDisk> = {}): DeployDisk => {
  const now = new Date().toISOString();
  return {
    id: "",
    attached: true,
    availabilityZone: "",
    baselineIops: 0,
    databaseId: "",
    provisionedIops: 3000,
    createdAt: now,
    updatedAt: now,
    currentKmsArn: "",
    device: "",
    ebsVolumeId: "",
    ebsVolumeType: "",
    ec2InstanceId: "",
    environmentId: "",
    filesystem: "",
    handle: "",
    host: "",
    size: 10,
    keyBytes: 32,
    ...d,
  };
};

export const defaultDeployPersistentDisk = (
  d: Partial<DeployPersistentDisk> = {},
): DeployPersistentDisk => {
  const now = new Date().toISOString();
  return {
    id: "",
    handle: "",
    size: 10,
    diskType: "gp3",
    status: "pending",
    environmentId: "",
    createdAt: now,
    updatedAt: now,
    ...d,
  };
};

export const defaultDeployDiskAttachment = (
  a: Partial<DeployDiskAttachment> = {},
): DeployDiskAttachment => {
  const now = new Date().toISOString();
  return {
    id: "",
    mountPoint: "",
    status: "attached",
    persistentDiskId: "",
    serviceId: "",
    createdAt: now,
    updatedAt: now,
    ...a,
  };
};

export const defaultDeployDatabase = (
  d: Partial<DeployDatabase> = {},
): DeployDatabase => {
  const now = new Date().toISOString();
  return {
    id: "",
    databaseImageId: "",
    status: "pending",
    handle: "",
    connectionUrl: "",
    createdAt: now,
    updatedAt: now,
    currentKmsArn: "",
    dockerRepo: "",
    provisioned: false,
    enableBackups: true,
    enablePitr: false,
    pitrStatus: "unsupported",
    oldestPitrTime: "",
    type: "",
    environmentId: "",
    serviceId: "",
    diskId: "",
    initializeFrom: "",
    portMapping: [],
    maintenanceWindowId: "",
    ...d,
  };
};

export const defaultDatabaseCredential = (
  p: Partial<DeployDatabaseCredential> = {},
): DeployDatabaseCredential => {
  return {
    id: "",
    databaseId: "",
    connectionUrl: "",
    type: "",
    default: false,
    ...p,
  };
};

export const defaultDeployDatabaseImage = (
  db: Partial<DeployDatabaseImage> = {},
): DeployDatabaseImage => {
  const now = new Date().toISOString();
  return {
    id: "",
    default: false,
    description: "",
    discoverable: false,
    dockerRepo: "",
    eolAt: "",
    indexable: false,
    type: "",
    version: "",
    visible: true,
    pitrSupported: false,
    createdAt: now,
    updatedAt: now,
    ...db,
  };
};

export const defaultDeployService = (
  s: Partial<DeployService> = {},
): DeployService => {
  const now = new Date().toISOString();
  return {
    id: "",
    appId: "",
    databaseId: "",
    environmentId: "",
    serviceSizingPolicyId: "",
    handle: "",
    dockerRef: "",
    dockerRepo: "",
    processType: "",
    command: "",
    containerCount: 0,
    containerMemoryLimitMb: 1024,
    currentReleaseId: "",
    instanceClass: DEFAULT_INSTANCE_CLASS,
    forceZeroDowntime: false,
    naiveHealthCheck: false,
    restartFreeScaling: false,
    stopTimeout: null,
    createdAt: now,
    updatedAt: now,
    ...s,
  };
};

export const defaultDeployLogDrain = (
  ld: Partial<DeployLogDrain> = {},
): DeployLogDrain => {
  const now = new Date().toISOString();
  return {
    id: "",
    handle: "",
    drainType: "tail",
    drainHost: "",
    drainPort: "",
    drainUsername: "",
    drainPassword: "",
    url: "",
    loggingToken: "",
    drainApps: false,
    drainProxies: false,
    drainEphemeralSessions: false,
    drainDatabases: false,
    environmentId: "",
    databaseId: "",
    backendChannel: "",
    createdAt: now,
    updatedAt: now,
    status: "pending",
    ...ld,
  };
};

export const defaultDeployMetricDrain = (
  md: Partial<DeployMetricDrain> = {},
): DeployMetricDrain => {
  const now = new Date().toISOString();
  return {
    id: "",
    handle: "",
    drainType: "",
    agggregatorCaCertificate: "",
    aggregatorCaPrivateKeyBlob: "",
    aggregatorHost: "",
    aggregatorPortMapping: [],
    aggregatorInstanceId: "",
    aggregatorDockerName: "",
    aggregatorAllocation: [],
    drainConfiguration: {},
    environmentId: "",
    databaseId: "",
    createdAt: now,
    updatedAt: now,
    status: "pending",
    ...md,
  };
};

export const defaultDeployOperation = (
  op: Partial<DeployOperation> = {},
): DeployOperation => {
  const now = new Date().toISOString();
  return {
    id: "",
    environmentId: "",
    codeScanResultId: "",
    resourceId: "",
    resourceType: "unknown",
    type: "unknown",
    status: "unknown",
    createdAt: now,
    updatedAt: now,
    gitRef: "",
    dockerRef: "",
    instanceProfile: "",
    containerCount: -1,
    containerSize: -1,
    diskSize: 0,
    encryptedEnvJsonNew: "",
    destinationRegion: "",
    cancelled: false,
    aborted: false,
    automated: false,
    immediate: false,
    provisionedIops: 0,
    ebsVolumeType: "",
    encryptedStackSettings: "",
    userName: "unknown",
    userEmail: "",
    env: {},
    note: "",
    ...op,
  };
};

export const defaultPlan = (c: Partial<DeployPlan> = {}): DeployPlan => {
  const now = new Date().toISOString();
  return {
    id: "",
    automatedBackupLimitPerDb: 0,
    complianceDashboardAccess: false,
    containerMemoryLimit: 0,
    costCents: 0,
    cpuAllowedProfiles: 0,
    createdAt: now,
    diskLimit: 0,
    environmentLimit: undefined,
    ephemeralSessionLimit: 0,
    includedContainerMb: 0,
    includedDiskGb: 0,
    includedVhosts: 0,
    manualBackupLimitPerDb: 0,
    name: "none",
    updatedAt: now,
    vhostLimit: 0,
    horizontalAutoscaling: false,
    verticalAutoscaling: false,
    ...c,
  };
};

export const defaultActivePlan = (
  c: Partial<DeployActivePlan> = {},
): DeployActivePlan => {
  const now = new Date().toISOString();
  return {
    id: "",
    automatedBackupLimitPerDb: 0,
    availablePlans: ["starter", "development", "growth", "scale", "production"],
    complianceDashboardAccess: false,
    containerMemoryLimit: 0,
    costCents: 0,
    cpuAllowedProfiles: 0,
    createdAt: now,
    diskLimit: 0,
    environmentLimit: undefined,
    ephemeralSessionLimit: 0,
    includedContainerMb: 0,
    includedDiskGb: 0,
    includedVhosts: 0,
    manualBackupLimitPerDb: 0,
    organizationId: "",
    updatedAt: now,
    vhostLimit: 0,
    horizontalAutoscaling: false,
    verticalAutoscaling: false,
    planId: "",
    ...c,
  };
};

export const defaultPermission = (r: Partial<Permission> = {}): Permission => {
  return {
    id: "",
    scope: "unknown",
    environmentId: "",
    roleId: "",
    inheritedFrom: "",
    ...r,
  };
};

export const defaultDeployRelease = (
  r: Partial<DeployRelease> = {},
): DeployRelease => {
  const now = new Date().toISOString();
  return {
    id: "",
    dockerRef: "",
    dockerRepo: "",
    createdAt: now,
    updatedAt: now,
    serviceId: "",
    ...r,
  };
};

export const defaultDeployContainer = (
  c: Partial<DeployContainer> = {},
): DeployContainer => {
  const now = new Date().toISOString();
  return {
    id: "",
    awsInstanceId: "",
    dockerName: "",
    host: "",
    layer: "",
    memoryLimit: 0,
    port: 0,
    portMapping: [],
    status: "",
    createdAt: now,
    updatedAt: now,
    releaseId: "",
    ...c,
  };
};

export const defaultDeployVpcPeer = (
  s: Partial<DeployVpcPeer> = {},
): DeployVpcPeer => {
  const now = new Date().toISOString();
  return {
    id: "",
    connectionId: "",
    connectionStatus: "",
    description: "",
    peerAccountId: "",
    peerVpcId: "",
    createdAt: now,
    updatedAt: now,
    stackId: "",
    ...s,
  };
};

export const defaultDeployVpnTunnel = (
  s: Partial<DeployVpnTunnel> = {},
): DeployVpnTunnel => {
  const now = new Date().toISOString();
  return {
    id: "",
    handle: "",
    phase1Alg: "",
    phase1DhGroup: "",
    phase1Lifetime: "",
    phase2Alg: "",
    phase2DhGroup: "",
    phase2Lifetime: "",
    ourGateway: "",
    ourNetworks: [],
    peerGateway: "",
    peerNetworks: [],
    perfectForwardSecrecy: "",
    state: "unknown",
    auto: "",
    tunnelAttributes: {
      connections: {},
      routed_connections: {},
      security_associations: {},
    },
    createdAt: now,
    updatedAt: now,
    stackId: "",
    ...s,
  };
};

export const defaultDeployEdge = (e: Partial<DeployEdge> = {}): DeployEdge => {
  const now = new Date().toISOString();
  return {
    id: "",
    relationshipType: "",
    relationshipStartedAt: null,
    relationshipEndedAt: null,
    createdAt: now,
    updatedAt: now,
    organizationId: "",
    sourceResourceId: "",
    sourceResourceType: "",
    destinationResourceId: "",
    destinationResourceType: "",
    metadata: {},
    ...e,
  };
};

export const defaultDeployBackup = (
  b: Partial<DeployBackup> = {},
): DeployBackup => {
  const now = new Date().toISOString();
  return {
    id: "",
    databaseHandle: "",
    awsRegion: "",
    createdByEmail: "",
    databaseId: "",
    environmentId: "",
    createdFromOperationId: "",
    copiedFromId: "",
    size: 0,
    manual: false,
    createdAt: now,
    ...b,
  };
};

export const defaultBackupRp = (
  bk: Partial<DeployBackupRetentionPolicy> = {},
): DeployBackupRetentionPolicy => {
  const now = new Date().toISOString();
  // based on https://github.com/aptible/deploy-api/blob/16e9334dc873b64cff2863d7bbd017e11a2caf24/app/models/backup_retention_policy.rb#L8
  return {
    id: "",
    daily: 30,
    monthly: 12,
    yearly: 6,
    pitrDays: 0,
    makeCopy: true,
    keepFinal: true,
    environmentId: "",
    createdAt: now,
    ...bk,
  };
};

export const defaultDeployImage = (
  img: Partial<DeployImage> = {},
): DeployImage => {
  const now = new Date().toISOString();
  return {
    id: "",
    gitRepo: "",
    gitRef: "Not used",
    dockerRepo: "",
    exposedPorts: [],
    dockerRef: "",
    createdAt: now,
    updatedAt: now,
    ...img,
  };
};

export const defaultServiceSizingPolicy = (
  m: Partial<DeployServiceSizingPolicy> = {},
): DeployServiceSizingPolicy => {
  const now = new Date().toISOString();
  return {
    id: "",
    environmentId: "",
    autoscaling: "vertical",
    scalingEnabled: false,
    defaultPolicy: false,
    metricLookbackSeconds: 300,
    percentile: 99,
    postScaleUpCooldownSeconds: 60,
    postScaleDownCooldownSeconds: 300,
    postReleaseCooldownSeconds: 60,
    memCpuRatioRThreshold: 4,
    memCpuRatioCThreshold: 2,
    memScaleUpThreshold: 0.9,
    memScaleDownThreshold: 0.75,
    minimumMemory: 2048,
    maximumMemory: null,
    minContainers: 2,
    maxContainers: 4,
    minCpuThreshold: 0.1,
    maxCpuThreshold: 0.9,
    scaleUpStep: 1,
    scaleDownStep: 1,
    useHorizontalScale: false,
    // v2
    label: "",
    createdAt: now,
    updatedAt: now,
    ...m,
  };
};

// v2 (scheduled HAS) — unreleased
export const defaultServiceSizingPolicyScheduleV2 = (
  m: Partial<DeployServiceSizingPolicyScheduleV2> = {},
): DeployServiceSizingPolicyScheduleV2 => {
  const now = new Date().toISOString();
  return {
    id: "",
    serviceSizingPolicyId: "",
    type: "daily",
    startDay: 1,
    startTime: "00:00",
    endDay: 1,
    endTime: "00:00",
    createdAt: now,
    updatedAt: now,
    ...m,
  };
};

export const defaultGlobalControlFramework = (
  f: Partial<DeployGlobalControlFramework> = {},
): DeployGlobalControlFramework => {
  return {
    id: "",
    frameworkId: "",
    name: "",
    category: "",
    displayName: "",
    metricHandle: "",
    ...f,
  };
};

export const defaultGlobalControl = (
  c: Partial<DeployGlobalControl> = {},
): DeployGlobalControl => {
  return {
    id: "",
    name: "",
    handle: "",
    description: "",
    area: "",
    callToAction: "",
    detailedDescription: "",
    hasRemediation: false,
    customFrontend: false,
    frameworks: [],
    frameworksAsString: "",
    frameworkHandles: [],
    isDedicatedOnly: false,
    isPremium: false,
    isComingSoon: false,
    implementationUiComponent: "",
    isOrganizationScoped: false,
    globalControlFrameworkIds: [],
    ...c,
  };
};

export const defaultDeployCustomerControl = (
  c: Partial<DeployCustomerControl> = {},
): DeployCustomerControl => {
  return {
    id: "",
    globalControlId: "",
    accountId: "",
    status: "",
    lastEvaluation: "",
    resourceIds: [],
    isEvaluating: false,
    isDisabled: false,
    isComingSoon: false,
    ...c,
  };
};

export const defaultDeployCustomerControlResource = (
  r: Partial<DeployCustomerControlResource> = {},
): DeployCustomerControlResource => {
  return {
    id: "",
    customerControlId: "",
    compliant: false,
    ignored: false,
    detailsText: "",
    resourceHandle: "",
    resourceType: "",
    resourceId: "",
    readableResourceType: "",
    linkToParameters: { path: "", parentId: null, modelId: "" },
    ...r,
  };
};

export const defaultInvitation = (i?: Partial<Invitation>): Invitation => {
  const now = new Date().toISOString();
  return {
    id: "",
    email: "",
    createdAt: now,
    updatedAt: now,
    organizationId: "",
    organizationName: "",
    inviterName: "",
    roleName: "",
    expired: false,
    ...i,
  };
};

export const defaultOrganization = (
  o: Partial<Organization> = {},
): Organization => ({
  id: "",
  name: "",
  billingDetailId: "",
  ssoEnforced: false,
  updatedAt: new Date().toISOString(),
  reauthRequired: false,
  address: "",
  city: "",
  zip: "",
  state: "",
  securityAlertEmail: "",
  securityAlertOwnerNotify: true,
  opsAlertEmail: "",
  opsAlertOwnerNotify: true,
  emergencyPhone: "",
  primaryPhone: "",
  ...o,
});

export const defaultOtp = (o: Partial<Otp> = {}): Otp => {
  return {
    id: "",
    uri: "",
    recoveryCodesUrl: "",
    currentUrl: "",
    ...o,
  };
};

export const defaultToken = (t: Partial<Token> = {}): Token => {
  return {
    tokenId: "",
    accessToken: "",
    userUrl: "",
    actorUrl: "",
    ...t,
  };
};

export const defaultUser = (u: Partial<User> = {}): User => {
  return {
    id: "",
    name: "Aptible",
    email: "",
    otpEnabled: false,
    superuser: false,
    readOnlyImpersonate: false,
    username: "",
    verified: false,
    currentOtpId: "",
    externalId: "",
    selectedOrganizationId: "",
    createdAt: new Date().toISOString(),
    allowAiGatewayAccess: false,
    ...u,
  };
};

export const defaultBillingDetail = (
  bt: Partial<BillingDetail> = {},
): BillingDetail => {
  return {
    id: "",
    paymentMethodUrl: "",
    organizationDetailsJson: {},
    invoicedExternally: false,
    externalInvoicePortalUrl: "",
    ...bt,
  };
};

export const defaultTheme = (): Theme => {
  if (typeof window === "undefined") {
    return "light";
  }
  if (typeof window.matchMedia !== "function") {
    return "light";
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

export const defaultModal = () => {
  return {
    type: ModalType.NONE,
    props: {} as any,
  };
};

export const defaultRole = (r: Partial<Role> = {}): Role => {
  const now = new Date().toISOString();
  return {
    id: "",
    organizationId: "",
    name: "",
    type: "platform_user",
    scimCreated: false,
    createdAt: now,
    updatedAt: now,
    ...r,
  };
};

export const defaultMembership = (m: Partial<Membership> = {}): Membership => {
  const now = new Date().toISOString();
  return {
    id: "",
    userId: "",
    roleId: "",
    privileged: false,
    createdAt: now,
    updatedAt: now,
    ...m,
  };
};

export const defaultDeployment = (a: Partial<Deployment> = {}): Deployment => {
  const now = new Date().toISOString();
  return {
    id: "",
    dockerImage: "",
    dockerRepositoryUrl: "",
    status: "unknown",
    gitRepositoryUrl: "",
    gitRef: "",
    gitCommitSha: "",
    gitCommitUrl: "",
    gitCommitMessage: "",
    gitCommitTimestamp: "",
    createdAt: now,
    updatedAt: now,
    appId: "",
    operationId: "",
    imageId: "",
    configurationId: "",
    sourceId: "",
    settingId: "",
    ...a,
  };
};

export const defaultDeploySource = (
  s: Partial<DeploySource> = {},
): DeploySource => {
  const now = new Date().toISOString();
  return {
    id: "",
    displayName: "Unknown",
    url: "",
    createdAt: now,
    updatedAt: now,
    ...s,
  };
};

export const defaultDeploySetting = (
  s: Partial<DeploySetting> = {},
): DeploySetting => {
  const now = new Date().toISOString();
  return {
    id: "",
    settings: {},
    sensitiveSettings: {},
    createdAt: now,
    updatedAt: now,
    ...s,
  };
};

export const defaultDeployMaintenanceWindow = (
  m: Partial<DeployMaintenanceWindow> = {},
): DeployMaintenanceWindow => {
  const now = new Date().toISOString();
  return {
    id: "",
    dayOfWeek: 0,
    startHour: 22,
    durationHours: 5,
    createdAt: now,
    updatedAt: now,
    ...m,
  };
};

export const defaultManualScaleRecommendation = (
  s: Partial<ManualScaleRecommendation> = {},
): ManualScaleRecommendation => {
  const now = new Date().toISOString();
  return {
    id: "",
    serviceId: "",
    cpuUsage: 0,
    ramUsage: 0,
    ramTarget: 0,
    costSavings: 0,
    recommendedInstanceClass: "",
    recommendedContainerMemoryLimitMb: 0,
    instanceClassAtTime: "",
    containerMemoryLimitMbAtTime: 0,
    metricPercentile: 0,
    createdAt: now,
    ...s,
  };
};

export const defaultCost = (s: Partial<DeployCost> = {}): DeployCost => {
  return {
    id: "",
    estCost: 0,
    resourceType: "",
    ...s,
  };
};

export const defaultCostRates = (
  s: Partial<DeployCostRates> = {},
): DeployCostRates => {
  return {
    hids_cost_per_month: 0.02,
    vpn_tunnel_cost_per_month: 99.0,
    stack_cost_per_month: 499.0,
    backup_cost_gb_per_month: 0.02,
    disk_cost_gb_per_month: 0.2,
    persistent_disk_cost_gb_per_month: 0.2,
    disk_iops_cost_per_month: 0.01,
    vhost_cost_per_hour: 0.06,
    shared_vhost_cost_per_hour: 0.04,
    m_class_gb_per_hour: 0.08,
    c_class_gb_per_hour: 0.1,
    r_class_gb_per_hour: 0.05,
    ...s,
  };
};

export const defaultDeployDashboard = (
  s: Partial<DeployDashboard> = {},
): DeployDashboard => {
  const now = new Date().toISOString();
  return {
    id: "",
    name: "",
    organizationId: "",
    resourceId: "",
    resourceType: "",
    symptoms: "",
    rangeBegin: "",
    rangeEnd: "",
    observationTimestamp: "",
    data: {},
    createdAt: now,
    updatedAt: now,
    ...s,
  };
};

export const defaultDeployExternalAwsAccount = (
  a: Partial<import("@app/types").DeployExternalAwsAccount> = {},
): import("@app/types").DeployExternalAwsAccount => {
  const now = new Date().toISOString();
  return {
    id: "",
    organizationId: "",
    awsAccountId: "",
    accountName: "",
    awsRegionPrimary: "",
    discoveryEnabled: false,
    discoveryFrequency: "",
    lastDiscoveryAt: "",
    status: "unknown",
    createdByUserEmail: "",
    createdByUserName: "",
    createdByUserId: "",
    createdAt: now,
    updatedAt: now,
    ...a,
  };
};

export const defaultDeployExternalAwsResource = (
  r: Partial<import("@app/types").DeployExternalAwsResource> = {},
): import("@app/types").DeployExternalAwsResource => {
  const now = new Date().toISOString();
  return {
    id: "",
    externalAwsAccountId: "",
    resourceType: "",
    resourceArn: "",
    resourceId: "",
    resourceName: "",
    region: "",
    metadata: {},
    tags: {},
    discoveredAt: "",
    lastSyncedAt: "",
    syncStatus: "unknown",
    createdAt: now,
    updatedAt: now,
    ...r,
  };
};

export const defaultDeployAppExternalAwsRdsConnection = (
  c: Partial<import("@app/types").DeployAppExternalAwsRdsConnection> = {},
): import("@app/types").DeployAppExternalAwsRdsConnection => {
  const now = new Date().toISOString();
  return {
    id: "",
    databaseName: "",
    databaseUser: "",
    superuser: false,
    appId: "",
    externalAwsResourceId: "",
    externalAwsAccountId: "",
    createdAt: now,
    updatedAt: now,
    ...c,
  };
};

export const defaultDeployLlmKeyUser = (
  u: Partial<DeployLlmKeyUser> = {},
): DeployLlmKeyUser => {
  return {
    id: "",
    name: "",
    email: "",
    ...u,
  };
};

export const defaultDeployLlmKey = (
  t: Partial<DeployLlmKey> = {},
): DeployLlmKey => {
  const now = new Date().toISOString();
  return {
    id: "",
    handle: "",
    note: "",
    gatewayUrl: "",
    createdBy: defaultDeployLlmKeyUser(),
    revokedAt: null,
    revokedBy: null,
    status: "ACTIVE",
    environmentId: "",
    createdAt: now,
    updatedAt: now,
    currentUsage: {
      spend: 0,
      inputTokens: 0,
      outputTokens: 0,
      successfulRequests: 0,
      failedRequests: 0,
      totalRequests: 0,
      totalTokens: 0,
    },
    ...t,
  };
};

export const defaultDeployLlmGatewayKey = (
  k: Partial<DeployLlmGatewayKey> = {},
): DeployLlmGatewayKey => {
  const now = new Date().toISOString();
  return {
    id: "",
    handle: "",
    organizationId: "",
    gatewayUrl: "",
    tokenPrefix: "",
    spendLimit: null,
    createdBy: defaultDeployLlmKeyUser(),
    revokedAt: null,
    revokedBy: null,
    status: "ACTIVE",
    createdAt: now,
    updatedAt: now,
    ...k,
  };
};

export const defaultLlmGatewayUsage = (
  u: Partial<LlmGatewayUsage> = {},
): LlmGatewayUsage => {
  const now = new Date().toISOString();
  return {
    id: "",
    organizationId: "",
    llmKeyId: "",
    startTime: now,
    endTime: now,
    totalRequests: 0,
    successfulRequests: 0,
    failedRequests: 0,
    averageLatencyMs: 0,
    totalTokens: 0,
    inputTokens: 0,
    outputTokens: 0,
    spend: 0,
    ...u,
  };
};

export const defaultLlmGatewayRequest = (
  r: Partial<LlmGatewayRequest> = {},
): LlmGatewayRequest => ({
  id: "",
  llmKeyId: "",
  timestamp: new Date().toISOString(),
  operation: "",
  stream: false,
  status: "success",
  latencyMs: 0,
  inputTokens: null,
  outputTokens: null,
  totalTokens: null,
  cost: null,
  model: "",
  ...r,
});

export const defaultLlmGatewayRequestPage = (
  p: Partial<LlmGatewayRequestPage> = {},
): LlmGatewayRequestPage => ({
  id: "",
  requestIds: [],
  total: 0,
  ...p,
});

export const defaultDeployLlmGatewayConfiguration = (
  t: Partial<DeployLlmGatewayConfiguration> = {},
): DeployLlmGatewayConfiguration => {
  const now = new Date().toISOString();
  return {
    id: "",
    organizationId: "",
    teamId: "",
    spendLimit: 0,
    trial: true,
    models: [],
    currentUsage: null,
    createdAt: now,
    updatedAt: now,
    ...t,
  };
};

export const defaultDeployLlmKeyRequest = (
  r: Partial<DeployLlmKeyRequest> = {},
): DeployLlmKeyRequest => {
  return {
    id: "",
    llmKeyId: "",
    status: "",
    startTime: "",
    endTime: "",
    model: "",
    inputTokens: 0,
    outputTokens: 0,
    spend: 0,
    durationSeconds: 0,
    totalTokens: 0,
    createdAt: "",
    updatedAt: "",
    ...r,
  };
};

export const defaultDeployLlmPolicy = (
  p: Partial<DeployLlmPolicy> = {},
): DeployLlmPolicy => {
  return {
    id: "",
    environmentId: "",
    spendLimit: 0,
    models: [],
    currentUsage: {
      spend: 0,
      inputTokens: 0,
      outputTokens: 0,
      successfulRequests: 0,
      failedRequests: 0,
      totalRequests: 0,
      totalTokens: 0,
    },
    ...p,
  };
};

export const defaultDeployLlmTraceDrain = (
  d: Partial<DeployLlmTraceDrain> = {},
): DeployLlmTraceDrain => {
  return {
    id: "",
    handle: "",
    url: "",
    llmPolicyId: "",
    environmentId: "",
    drainType: "langfuse",
    publicKey: "",
    apiKey: "",
    status: "unknown",
    createdAt: "",
    updatedAt: "",
    ...d,
  };
};

export const defaultUserAgreement = (
  u: Partial<UserAgreement> = {},
): UserAgreement => {
  return {
    id: "",
    userId: "",
    agreementType: "",
    agreementVersion: "",
    createdAt: "",
    updatedAt: "",
    ...u,
  };
};
